/**
 * /auto-ecoles — Hub principal : index des 100+ départements.
 * Base juridique : recommandations CNIL sur les annuaires professionnels.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Search, Sparkles, ShieldCheck, ChevronRight, Building2 } from 'lucide-react';
import { API, SITE_NAME, useSeoMeta, formatDateFR } from './_shared';

export default function AutoEcolesHub() {
  const [depts, setDepts] = useState([]);
  const [stats, setStats] = useState({ count: 0 });
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/annuaire/dept-hubs`).then(r => r.json()),
      fetch(`${API}/api/annuaire/stats`).then(r => r.json()),
    ]).then(([d, s]) => {
      setDepts(d || []);
      setStats(s || {});
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useSeoMeta({
    title: `Annuaire des auto-écoles en France · ${SITE_NAME}`,
    description: `Trouve la auto-école agréée près de chez toi parmi ${stats.count?.toLocaleString('fr-FR') || '5 000+'} établissements référencés. Permis B, AM, B96, BE · Sources officielles Sécurité Routière.`,
    canonicalPath: '/auto-ecoles',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `Annuaire des auto-écoles en France`,
      description: 'Annuaire national des auto-écoles agréées Sécurité Routière (permis B, AM, B96, BE).',
      url: 'https://codevoiture.fr/auto-ecoles',
    },
  });

  const filtered = useMemo(() => {
    if (!q.trim()) return depts;
    const nq = q.toLowerCase().trim();
    return depts.filter(d => d.code.toLowerCase().includes(nq) || d.name.toLowerCase().includes(nq));
  }, [depts, q]);

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white pb-16" data-testid="auto-ecoles-hub">
      {/* HERO */}
      <header className="border-b border-white/10 bg-gradient-to-b from-rose-950/40 via-transparent to-transparent">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/15 border border-rose-500/30 text-rose-200 text-[10px] font-black uppercase tracking-widest mb-3">
            <Building2 className="w-3 h-3" /> Annuaire national · sources officielles
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Annuaire des auto-écoles<br className="hidden sm:block" /> en France
          </h1>
          <p className="text-white/70 text-sm sm:text-base mt-3 max-w-2xl leading-relaxed">
            <strong className="text-white">{stats.count?.toLocaleString('fr-FR') || '5 000+'} auto-écoles agréées</strong> référencées à partir des données publiques de la Sécurité Routière. Permis B, AM, B96, BE.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3 text-[11px] text-white/50">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.03] border border-white/10">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> Source : Sécurité Routière · data.gouv.fr
            </span>
            {stats.updated_at && <span className="text-white/40">Mise à jour : {formatDateFR(stats.updated_at)}</span>}
          </div>
        </div>
      </header>

      {/* Recherche */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
          <input
            type="text"
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Rechercher un département (ex. 75 · Paris · Rhône…)"
            className="w-full h-12 pl-11 pr-4 rounded-2xl bg-[#141416] border border-white/10 text-white placeholder-white/30 text-sm focus:outline-none focus:border-rose-500/50 focus:ring-2 focus:ring-rose-500/20"
            data-testid="auto-ecoles-hub-search"
          />
        </div>

        <Link
          to="/annuaire"
          className="mt-4 inline-flex items-center gap-2 px-4 h-10 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-[12px] font-black uppercase tracking-widest transition"
          data-testid="auto-ecoles-carte-cta"
        >
          <MapPin className="w-3.5 h-3.5" /> Voir la carte de France interactive
        </Link>
      </div>

      {/* Grille départements */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8">
        <h2 className="text-lg font-black text-white/90 mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          {filtered.length} département{filtered.length > 1 ? 's' : ''} référencé{filtered.length > 1 ? 's' : ''}
        </h2>
        {loading ? (
          <div className="text-white/40 text-sm">Chargement…</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2.5">
            {filtered.map(d => (
              <Link
                key={d.code}
                to={`/auto-ecoles/${d.code}`}
                className="group rounded-xl bg-[#141416] border border-white/10 hover:border-rose-500/40 hover:bg-[#181819] transition p-3 flex items-center justify-between gap-2"
                data-testid={`dept-tile-${d.code}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-rose-300 font-mono text-[11px] font-bold">{d.code}</div>
                  <div className="text-white text-[13px] font-bold truncate leading-tight">{d.name}</div>
                  <div className="text-white/40 text-[10.5px] mt-0.5">{d.count} école{d.count > 1 ? 's' : ''}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-rose-400 shrink-0 transition" />
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Source / mentions légales */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-10 pt-6 border-t border-white/5 text-[11.5px] text-white/40 space-y-1">
        <p>
          <Sparkles className="inline w-3 h-3 mr-1" />
          Données publiques réutilisées depuis <a href="https://www.data.gouv.fr/datasets/liste-des-auto-ecoles-et-taux-de-reussite-au-permis-de-conduire/" target="_blank" rel="noopener noreferrer" className="underline hover:text-rose-300">data.gouv.fr — Sécurité Routière</a>. Aucune donnée personnelle du dirigeant n'est affichée.
        </p>
        <p>
          <Link to="/mentions-annuaire" className="underline hover:text-rose-300">Mentions légales &amp; politique de confidentialité</Link> · <Link to="/annuaire" className="underline hover:text-rose-300">Recherche avancée</Link>
        </p>
      </div>
    </div>
  );
}
